import { MaterialIcons } from "@expo/vector-icons";
import { useNavigation } from "@react-navigation/native";
// NOTE: Adding @sentry/react to the dependencies causes an error because of dependencies it installs.
// Our other sentry dependencies (@sentry/react-native) already install @sentry/react so we can ignore this error.
// eslint-disable-next-line import/no-extraneous-dependencies
import * as Sentry from "@sentry/react";
import { AVPlaybackStatus, ResizeMode, Video } from "expo-av";
import { Box, Button, Icon, Pressable, Spinner, Text, useTheme, VStack } from "native-base";
import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dimensions, Platform } from "react-native";
import { useSelector } from "react-redux";

import ScreenContainer from "../../components/ScreenContainer";
import { ONBOARDING_NAVIGATION_ERROR } from "../../constants/onboardingMessages";
import ScreenNames from "../../constants/routeNames";
import { getOrganisation } from "../../helpers/userHelpers";
import { getNextScreen } from "../../navigation/simpleOnboardingFlow";
import { userSelector } from "../../slices/userSlice";

const SimpleOnboardingWelcomeVideoScreen = (): JSX.Element => {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const theme = useTheme();
  const user = useSelector(userSelector);
  const organisation = user ? getOrganisation(user) : undefined;
  const appName = organisation?.name || "NutriAI";

  const videoRef = useRef<Video | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Calculate portrait aspect ratio (9:16)
  const screenWidth = Dimensions.get("window").width;
  const videoHeight = (screenWidth - 48) * (16 / 9); // 16:9 aspect ratio for portrait video

  const getVideoUrl = useCallback((): string | null => {
    // Check if organisation has video enabled and URL
    if (organisation?.welcome_video_enabled && organisation?.welcome_video) {
      return organisation.welcome_video; // Full S3 URL from API
    }

    return null;
  }, [organisation]);

  const handleContinue = useCallback(async (): Promise<void> => {
    if (videoRef.current && isPlaying) {
      await videoRef.current.pauseAsync();
    }

    const nextScreen = getNextScreen(ScreenNames.SimpleOnboarding_0_WelcomeVideo);
    if (nextScreen) {
      navigation.navigate(nextScreen as never);
    } else {
      // eslint-disable-next-line no-console
      console.error(ONBOARDING_NAVIGATION_ERROR);
      throw new Error(ONBOARDING_NAVIGATION_ERROR);
    }
  }, [isPlaying, navigation]);

  const handlePlaybackStatusUpdate = useCallback(
    (status: AVPlaybackStatus): void => {
      if (status.isLoaded) {
        setIsPlaying(status.isPlaying);
        if (status.durationMillis) {
          setProgress(status.positionMillis / status.durationMillis);
        }
        if (status.didJustFinish) {
          handleContinue();
        }
      }
    },
    [handleContinue]
  );

  const handleVideoLoad = useCallback((): void => {
    setIsLoading(false);
    setHasError(false);

    // Auto-play when video loads
    if (videoRef.current) {
      videoRef.current.playAsync();
    }
  }, []);

  const handleVideoError = useCallback((): void => {
    setIsLoading(false);
    setHasError(true);

    // Send error to Sentry
    Sentry.captureException(new Error("Welcome video failed to load"), {
      tags: {
        screen: "SimpleOnboarding_0_WelcomeVideoScreen",
        videoUrl: getVideoUrl(),
        organisation: organisation?.name || "unknown",
      },
    });

    // Navigate to next screen after error
    const nextScreen = getNextScreen(ScreenNames.SimpleOnboarding_0_WelcomeVideo);
    if (nextScreen) {
      navigation.navigate(nextScreen as never);
    } else {
      // eslint-disable-next-line no-console
      console.error(ONBOARDING_NAVIGATION_ERROR);
      throw new Error(ONBOARDING_NAVIGATION_ERROR);
    }
  }, [organisation, navigation, getVideoUrl]);

  const handlePlayPause = useCallback(async (): Promise<void> => {
    if (!videoRef.current) return;

    if (isPlaying) {
      await videoRef.current.pauseAsync();
    } else {
      await videoRef.current.playAsync();
    }
  }, [isPlaying]);

  const handleFullscreen = useCallback(async (): Promise<void> => {
    if (!videoRef.current) return;

    try {
      if (isFullscreen) {
        await videoRef.current.dismissFullscreenPlayer();
      } else {
        await videoRef.current.presentFullscreenPlayer();
      }
    } catch (error) {
      // Handle iOS bug where presentFullscreenPlayer only works once
      // eslint-disable-next-line no-console
      console.error("Fullscreen error:", error);

      // If on iOS and fullscreen fails, try to use native controls as fallback
      if (!isFullscreen && error instanceof Error && error.message?.includes("already being presented")) {
        // Force update the video component by pausing and playing
        await videoRef.current.pauseAsync();
        await videoRef.current.playAsync();
      }
    }
  }, [isFullscreen]);

  const handleFullscreenUpdate = useCallback((event: { fullscreenUpdate: number }): void => {
    setIsFullscreen(event.fullscreenUpdate === 1 || event.fullscreenUpdate === 3);
  }, []);

  return (
    <ScreenContainer safeAreaMode="full">
      <Box flex={1}>
        {/* Video content */}
        <VStack flex={1} justifyContent="center" alignItems="center" px={6}>
          {getVideoUrl() ? (
            <Pressable
              onPress={handlePlayPause}
              width="100%"
              height={videoHeight}
              mb={8}
              position="relative"
              overflow="hidden"
              borderRadius={theme.borderRadii.card.default}
              bg="black"
              justifyContent="center"
              alignItems="center"
            >
              <Video
                ref={videoRef}
                source={{ uri: getVideoUrl()! }}
                // eslint-disable-next-line global-require
                posterSource={require("../../assets/images/welcome-video-poster.jpg")}
                rate={1.0}
                volume={1.0}
                isMuted={false}
                resizeMode={ResizeMode.CONTAIN}
                shouldPlay={false}
                isLooping={false}
                useNativeControls={false}
                onPlaybackStatusUpdate={handlePlaybackStatusUpdate}
                onLoad={handleVideoLoad}
                onError={handleVideoError}
                onFullscreenUpdate={handleFullscreenUpdate}
                style={Platform.select({
                  web: {
                    position: "relative", // Critical fix for web scaling
                    width: "100%",
                    height: "100%",
                    borderRadius: theme.borderRadii.card.default,
                  },
                  default: {
                    width: "100%",
                    height: "100%",
                    borderRadius: theme.borderRadii.card.default,
                  },
                })}
                // Add videoStyle prop to fix web scaling - targets the inner video element
                videoStyle={
                  Platform.OS === "web"
                    ? {
                        position: "relative",
                        width: "100%",
                        height: "100%",
                      }
                    : undefined
                }
              />

              {/* Loading spinner */}
              {isLoading ? (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  justifyContent="center"
                  alignItems="center"
                  bg="rgba(0,0,0,0.5)"
                  borderRadius={theme.borderRadii.card.default}
                >
                  <Spinner size="lg" color="white" />
                  <Text color="white" mt={2} fontFamily="body">
                    {t("simple_onboarding.welcome.loading_message")}
                  </Text>
                </Box>
              ) : null}

              {/* Error state */}
              {hasError ? (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  justifyContent="center"
                  alignItems="center"
                  bg="gray.100"
                  borderRadius={theme.borderRadii.card.default}
                >
                  <Text color="function.30" fontFamily="body">
                    {t("simple_onboarding.welcome.error_message")}
                  </Text>
                </Box>
              ) : null}

              {/* Play/pause overlay when not playing and loaded */}
              {!isPlaying && !isLoading && !hasError && (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  justifyContent="center"
                  alignItems="center"
                >
                  <Box
                    width={0}
                    height={0}
                    borderLeftWidth={30}
                    borderLeftColor="rgba(0,0,0,0.7)"
                    borderTopWidth={18}
                    borderTopColor="transparent"
                    borderBottomWidth={18}
                    borderBottomColor="transparent"
                  />
                </Box>
              )}

              {/* Progress bar */}
              <Box
                position="absolute"
                bottom={0}
                left={0}
                right={0}
                height={1}
                bg="gray.300"
                borderBottomRadius={theme.borderRadii.card.default}
              >
                <Box
                  height="100%"
                  width={`${progress * 100}%`}
                  bg="primary.50"
                  borderBottomLeftRadius={theme.borderRadii.card.default}
                  borderBottomRightRadius={progress >= 0.99 ? theme.borderRadii.card.default : 0}
                />
              </Box>

              {/* Fullscreen button */}
              {!isLoading && !hasError && (
                <Pressable
                  onPress={handleFullscreen}
                  position="absolute"
                  top={2}
                  right={2}
                  bg="rgba(0,0,0,0.5)"
                  borderRadius={theme.borderRadii.button.small}
                  p={2}
                  accessibilityLabel={t("simple_onboarding.welcome.fullscreen_button")}
                  accessibilityRole="button"
                >
                  <Icon
                    as={MaterialIcons}
                    name={isFullscreen ? "fullscreen-exit" : "fullscreen"}
                    size={6}
                    color="white"
                  />
                </Pressable>
              )}
            </Pressable>
          ) : (
            /* Fallback when no video */
            <Box
              width="100%"
              height={videoHeight}
              bg="gray.100"
              borderRadius={theme.borderRadii.card.default}
              justifyContent="center"
              alignItems="center"
              mb={8}
            >
              <Text color="function.30" fontSize="16" fontFamily="body">
                {t("simple_onboarding.welcome.video_placeholder")}
              </Text>
            </Box>
          )}

          <Text
            fontSize="32"
            fontFamily="serif"
            fontStyle="italic"
            textAlign="center"
            mb={4}
            color="function.50"
            lineHeight="32"
          >
            {t("simple_onboarding.welcome.welcome_title", { appName })}
          </Text>
        </VStack>

        {/* Continue button */}
        <Box px={6} pb={6}>
          <Button
            bg="primary.50"
            borderRadius={theme.borderRadii.button.default}
            _text={{
              fontFamily: "body",
              fontWeight: "500",
              fontSize: "16",
              color: "primary.10",
            }}
            _pressed={{
              bg: "primary.50",
            }}
            _disabled={{
              bg: "function.20",
              _text: {
                color: "function.30",
              },
            }}
            onPress={handleContinue}
            size="lg"
            testID="continue-button"
          >
            {t("simple_onboarding.welcome.continue_button")}
          </Button>
        </Box>
      </Box>
    </ScreenContainer>
  );
};

export default SimpleOnboardingWelcomeVideoScreen;
